import type { JSX } from "react";
import { useEffect, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useSceneStore } from "@/state/useSceneStore";

const VERTEX = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAGMENT = `
  uniform float uTime;
  uniform float uPowerOn;
  uniform vec3 uColor;
  varying vec2 vUv;
  float rand(vec2 c) { return fract(sin(dot(c, vec2(12.9898, 78.233))) * 43758.5453); }
  void main() {
    vec2 uv = vUv;
    float scan = sin((uv.y + uTime * 0.5) * 800.0) * 0.04;
    float noise = rand(uv * uTime) * 0.06;
    float power = smoothstep(0.0, 0.8, uPowerOn);
    vec3 col = uColor * (0.4 + scan + noise) * power;
    gl_FragColor = vec4(col, 1.0);
  }
`;

export const MonitorScreen = (): JSX.Element | null => {
  const screen = useSceneStore((s) => s.objects.monitorScreen);

  const material = useMemo(
    () =>
      new THREE.ShaderMaterial({
        uniforms: {
          uTime: { value: 0 },
          uPowerOn: { value: 1 },
          uColor: { value: new THREE.Color("#7af0a8") },
        },
        vertexShader: VERTEX,
        fragmentShader: FRAGMENT,
      }),
    [],
  );

  useEffect(() => {
    if (!(screen instanceof THREE.Mesh)) return;
    const original = screen.material;
    screen.material = material;
    return () => {
      screen.material = original;
      material.dispose();
    };
  }, [screen, material]);

  useFrame(({ clock }) => {
    const u = material.uniforms["uTime"];
    if (u) u.value = clock.elapsedTime;
  });

  return null;
};
